import useSWR from 'swr'

import { useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiDeleteFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  BulkResponse,
  BulkVars,
  DeletedResponse,
  DetailVars,
  ExternalArticle,
  ExternalArticleApiPath,
  ExternalArticleSettings,
  ListExternalArticleResponse,
  ListExternalArticleVars,
  RefetchResponse,
  SiteIdVars,
  UpdateSettingsVars
} from '@/common/api/params/externalArticle'

/** 外部記事の一覧。状態ごとの件数と取得待ちの残件数も返る */
export const useExternalArticlesApi = (
  vars: ListExternalArticleVars,
  refreshInterval?: number
) =>
  useSWR<ListExternalArticleResponse, Response, [string, ListExternalArticleVars]>(
    [ExternalArticleApiPath.LIST, vars],
    apiDefaultFetcher,
    // 取得はサーバ側のワーカーが行うため、取得待ちがある間だけ自動で追従させる
    refreshInterval ? { refreshInterval } : undefined
  )

/** 1件の詳細（抽出結果の確認） */
export const useExternalArticleApi = (vars: DetailVars | null) =>
  useSWR<ExternalArticle, Response, [string, DetailVars] | null>(
    vars ? [ExternalArticleApiPath.DETAIL, vars] : null,
    apiDefaultFetcher
  )

/** 複数行のURL貼り付けによる一括投入。取得はワーカーが行う */
export const useExternalArticleBulkApi = (onSuccess?: (r: BulkResponse) => void) =>
  useCallMutation<BulkResponse, BulkVars>(ExternalArticleApiPath.BULK, { onSuccess })

/** 1件を取得待ちへ戻す */
export const useExternalArticleRefetchApi = (
  onSuccess?: (r: RefetchResponse) => void,
  onError?: (e: Response) => void
) =>
  useCallMutation<RefetchResponse, DetailVars>(ExternalArticleApiPath.REFETCH, {
    onSuccess,
    onError
  })

/** 全件を取得待ちへ戻す（抽出セレクタを変えた後の作り直し） */
export const useExternalArticleRefetchAllApi = (onSuccess?: (r: RefetchResponse) => void) =>
  useCallMutation<RefetchResponse, SiteIdVars>(ExternalArticleApiPath.REFETCH_ALL, { onSuccess })

/** 1件を物理削除する。付随するAIタグ・ベクトルも消える */
export const useExternalArticleDeleteApi = (
  onSuccess?: (r: DeletedResponse) => void,
  onError?: (e: Response) => void
) =>
  useCallMutation<DeletedResponse, DetailVars>(
    ExternalArticleApiPath.DETAIL,
    { onSuccess, onError },
    apiDeleteFetcher
  )

/** 取り込み設定。パスワードの値は返らない（設定済みかどうかだけ） */
export const useExternalArticleSettingsApi = (vars: SiteIdVars) =>
  useSWR<ExternalArticleSettings, Response, [string, SiteIdVars]>(
    [ExternalArticleApiPath.SETTINGS, vars],
    apiDefaultFetcher
  )

export const useExternalArticleSettingsUpdateApi = (
  onSuccess?: (r: ExternalArticleSettings) => void
) =>
  useCallMutation<ExternalArticleSettings, UpdateSettingsVars>(
    ExternalArticleApiPath.SETTINGS,
    { onSuccess },
    apiPutFetcher
  )
